/**
 * GeoTIFF (raster) import.
 *
 * Every other local format in this folder ends up as GeoJSON features and
 * joins the vector pipeline `localData` already has — a symbology editor, an
 * attribute table, a pop-up. A GeoTIFF has none of that to join: it is a grid
 * of pixels with its own georeferencing baked into the file, not a set of
 * shapes.
 *
 * WHY THIS COMPRESSES RATHER THAN STORES THE FILE AS-IS
 * A GeoTIFF travels with the module exactly as every other local layer does —
 * base64 inside its JSON — and an unmodified GeoTIFF is often barely
 * compressed at all: many GIS exports use no compression, or a mild lossless
 * one. Five files totalling 50 MB became roughly 66 MB of base64 sitting in
 * the browser's memory and the module's saved JSON, re-parsed on every load
 * and re-sent on every save — that is what made the app slow, not any one
 * step in particular.
 *
 * So this module DECODES the raster's pixels once, at import time, and
 * re-encodes them as a compressed image (WebP, or PNG on a browser too old to
 * support it) capped at `RASTER_MAX_DIMENSION` — the same trade a satellite
 * basemap tile makes. What is stored and what MapView draws (`LocalRasterLayer`
 * in MapView.tsx, via a plain `L.imageOverlay`) is that image plus its WGS84
 * bounds, not the original file: multi-band precision and any value beyond
 * 8-bit RGB is gone, and a photo taken at high resolution is downsampled. For
 * an aerial photo or a scanned map — what this feature exists for — that is
 * invisible on screen and cuts the stored size by roughly an order of
 * magnitude. For a raster where the actual numbers matter (elevation,
 * multispectral analysis), this is the wrong tool — that belongs on GeoServer,
 * unmodified.
 *
 * A LAYER SAVED BEFORE THIS EXISTED still carries the raw bytes it was
 * imported with (see `LocalRasterData.mimeType` in types.ts — absent on the
 * old shape, set here on the new one). `LocalRasterLayer` still knows how to
 * draw that: this only changes what a NEW import produces.
 *
 * PROJECTION
 * A GeoTIFF's extent (`xmin`/`ymin`/`xmax`/`ymax`) is in whatever CRS the file
 * was written in — almost always a UTM zone for anything captured on the
 * ground. What this module has to get right is `layer.bounds`, the WGS84 box
 * the compressed image is placed at and the rest of the app uses for
 * zoom-to-layer — done with the exact same `resolveCrs` the shapefile and
 * GeoJSON readers use, so a UTM GeoTIFF is understood exactly as well as a
 * UTM shapefile is.
 */

import parseGeoraster from 'georaster';
import { resolveCrs } from './reproject';
import { resolutionAt } from '../labeling/ScaleVisibility';

export class GeoTiffError extends Error {}

/**
 * The SOURCE file's ceiling — how much a browser tab is asked to download and
 * decode into raw pixel arrays. Unlike before this feature compressed the
 * output, this no longer bounds what ends up stored: a 20 MB source routinely
 * becomes a 1-2 MB image. It stays a fairly modest number anyway, because
 * decoding is still real work done on this one tab's memory.
 */
export const MAX_RASTER_FILE_BYTES = 20 * 1024 * 1024;

/**
 * Longest side of the re-encoded image, in pixels. Large enough to look sharp
 * at any zoom level anyone actually reads an inset image at; small enough
 * that the encoded file stays a couple of MB even for a source many times
 * this size.
 */
export const RASTER_MAX_DIMENSION = 2048;

/** WebP quality, 0-1. Photographic content — the intended use — holds up well
 *  well past this; pushed lower only trades a few more KB for banding no one
 *  asked for. */
const RASTER_IMAGE_QUALITY = 0.85;

export interface ParsedGeoTiff {
  base64: string;
  /** image/webp normally; image/png on a browser whose canvas can't encode WebP. */
  mimeType: string;
  /** The RE-ENCODED image's pixel size, after any downsampling — what is
   *  actually stored and drawn, not necessarily the source file's own. */
  width: number;
  height: number;
  /** The SOURCE file's band count, for the Layer Information panel — the
   *  encoded image is always RGBA, so this would otherwise be lost entirely. */
  bandCount: number;
  projectionEpsg?: number;
  bounds: [number, number, number, number];
  notices: string[];
}

/**
 * Chunked so a multi-MB image never spreads millions of bytes into one
 * `String.fromCharCode` call — that overflows the call stack long before it
 * overflows anything this function is actually limited by.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

/** The raster's extent, converted to WGS84 for `layer.bounds`. */
function boundsToWgs84(
  xmin: number,
  ymin: number,
  xmax: number,
  ymax: number,
  epsg: number,
): { bounds: [number, number, number, number]; notice?: string } {
  if (epsg === 4326) return { bounds: [xmin, ymin, xmax, ymax] };

  const resolved = resolveCrs(`EPSG:${epsg}`);
  if (resolved.error || !resolved.convert) {
    // The image is placed at these raw numbers regardless — wrong, but still
    // a rectangle roughly where the real one is — so this is a notice, not a
    // refusal.
    return {
      bounds: [xmin, ymin, xmax, ymax],
      notice:
        `Could not resolve EPSG:${epsg} for this file's stored extent, so ` +
        'the image may be placed in the wrong location.',
    };
  }

  const corners = [
    resolved.convert(xmin, ymin),
    resolved.convert(xmax, ymin),
    resolved.convert(xmin, ymax),
    resolved.convert(xmax, ymax),
  ];
  const lons = corners.map((corner) => corner[0]);
  const lats = corners.map((corner) => corner[1]);
  return {
    bounds: [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)],
  };
}

/** 0-255. A raw 16- or 32-bit band would otherwise render solid black or
 *  solid white — this stretches whatever range the band actually uses onto
 *  the range a screen can show, the same thing a "linear stretch" renderer
 *  does in any desktop GIS. */
function stretch(value: number, min: number, max: number): number {
  if (max <= min) return 128;
  return Math.max(0, Math.min(255, Math.round(((value - min) / (max - min)) * 255)));
}

/**
 * Resamples the raster's bands onto a capped-resolution RGBA grid.
 *
 * Nearest-neighbour, not area-averaged: the cost of building this is bounded
 * by the OUTPUT size, never the source's, because every sample this function
 * takes lands on one exact source pixel rather than blending several — and at
 * the sizes this produces, that saving is invisible on screen. `values` is a
 * live reference into what `parseGeoraster` already decoded, so nothing here
 * re-reads the file.
 */
function buildImageData(meta: {
  width: number;
  height: number;
  numberOfRasters: number;
  noDataValue: number | null;
  values: number[][][];
}): { imageData: ImageData; width: number; height: number } {
  const srcWidth = meta.width;
  const srcHeight = meta.height;
  const scale = Math.min(1, RASTER_MAX_DIMENSION / Math.max(srcWidth, srcHeight));
  const width = Math.max(1, Math.round(srcWidth * scale));
  const height = Math.max(1, Math.round(srcHeight * scale));

  const bandCount = meta.values.length;
  const bandsUsed = Math.min(bandCount, 4);
  const sampleAt = (band: number, x: number, y: number): number => {
    const sourceX = Math.min(srcWidth - 1, Math.floor((x / width) * srcWidth));
    const sourceY = Math.min(srcHeight - 1, Math.floor((y / height) * srcHeight));
    return meta.values[band][sourceY][sourceX];
  };

  // Per-band min/max at OUTPUT resolution — same reason as above: this scans
  // exactly the pixels that will be painted, never the source's full grid.
  const mins = new Array(bandsUsed).fill(Infinity);
  const maxs = new Array(bandsUsed).fill(-Infinity);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      for (let band = 0; band < bandsUsed; band += 1) {
        const value = sampleAt(band, x, y);
        if (meta.noDataValue != null && value === meta.noDataValue) continue;
        if (value < mins[band]) mins[band] = value;
        if (value > maxs[band]) maxs[band] = value;
      }
    }
  }

  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const raw: number[] = [];
      for (let band = 0; band < bandsUsed; band += 1) raw.push(sampleAt(band, x, y));
      const isNoData = meta.noDataValue != null && raw[0] === meta.noDataValue;

      if (bandCount === 1) {
        // Single-band imagery (panchromatic, a scanned grayscale map) — one
        // stretched value copied into all three channels.
        const gray = stretch(raw[0], mins[0], maxs[0]);
        data[offset] = gray;
        data[offset + 1] = gray;
        data[offset + 2] = gray;
      } else {
        data[offset] = stretch(raw[0], mins[0], maxs[0]);
        data[offset + 1] = stretch(raw[1] ?? raw[0], mins[1] ?? mins[0], maxs[1] ?? maxs[0]);
        data[offset + 2] = stretch(raw[2] ?? raw[0], mins[2] ?? mins[0], maxs[2] ?? maxs[0]);
      }
      data[offset + 3] = isNoData ? 0 : bandsUsed >= 4 ? stretch(raw[3], mins[3], maxs[3]) : 255;
    }
  }

  return { imageData: new ImageData(data, width, height), width, height };
}

/**
 * Encodes a canvas's current pixels as a compressed image file — the shared
 * tail end of both `encodeCompressedImage` (a GeoTIFF's resampled grid) and
 * `decodePlainImagePixels` (a plain photo/scan downsampled onto a canvas).
 * Exported for the Georeference tool's own warped-canvas output, the one
 * caller outside this file that builds a canvas of its own rather than
 * decoding one from a source file.
 */
export async function canvasToCompressedImage(canvas: HTMLCanvasElement): Promise<{ base64: string; mimeType: string }> {
  // A browser that cannot encode WebP silently returns a PNG blob instead of
  // failing — `blob.type` is read back rather than assumed, so the stored
  // `mimeType` always matches what was actually produced.
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new GeoTiffError('Could not encode the image.'))),
      'image/webp',
      RASTER_IMAGE_QUALITY,
    );
  });

  const buffer = await blob.arrayBuffer();
  return { base64: arrayBufferToBase64(buffer), mimeType: blob.type || 'image/png' };
}

/** Encodes pixels built by `buildImageData` as a compressed image file. */
async function encodeCompressedImage(imageData: ImageData): Promise<{ base64: string; mimeType: string }> {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new GeoTiffError('This browser could not render the compressed image.');
  }
  context.putImageData(imageData, 0, 0);
  return canvasToCompressedImage(canvas);
}

/**
 * Where a raster with no georeferencing of its own — a plain PNG/JPG, as
 * opposed to a GeoTIFF's embedded extent — lands on the map: centred on
 * whatever the user is currently looking at, at roughly its own pixel size
 * (one image pixel per screen pixel at the current zoom), the same
 * "paste it in at the size it already is" a desktop editor would do. There is
 * no interactive resize/reposition for a raster layer in this app today, so
 * this placement is a starting point to zoom/pan around, not a fixed fact.
 */
function boundsForImageAtView(
  pixelWidth: number,
  pixelHeight: number,
  center: [number, number],
  zoom: number,
): [number, number, number, number] {
  const metresPerPixel = resolutionAt(center[0], zoom);
  const halfWidthM = (pixelWidth / 2) * metresPerPixel;
  const halfHeightM = (pixelHeight / 2) * metresPerPixel;
  const metresPerDegLat = 111320;
  const metresPerDegLng = Math.max(metresPerDegLat * Math.cos((center[0] * Math.PI) / 180), 1e-6);
  return [
    center[1] - halfWidthM / metresPerDegLng,
    center[0] - halfHeightM / metresPerDegLat,
    center[1] + halfWidthM / metresPerDegLng,
    center[0] + halfHeightM / metresPerDegLat,
  ];
}

export interface ParsedPlainImage {
  base64: string;
  mimeType: string;
  width: number;
  height: number;
  bandCount: number;
  bounds: [number, number, number, number];
  notices: string[];
}

interface DecodedPlainImagePixels {
  base64: string;
  mimeType: string;
  width: number;
  height: number;
  downsampleNotice?: string;
}

/**
 * The decode-only half of a plain PNG/JPG import — read as-is when already
 * small, downsampled and recompressed above `RASTER_MAX_DIMENSION` the same
 * way a GeoTIFF is. No opinion on where the result belongs on a map: that is
 * `parsePlainImageFile`'s own `boundsForImageAtView` guess for a normal Add
 * Data import, or a control-point fit for the Georeference tool's upload
 * path (`decodeRasterForGeoreferencing`) — both call this rather than
 * duplicating the decode.
 */
async function decodePlainImagePixels(file: File): Promise<DecodedPlainImagePixels> {
  if (file.size > MAX_RASTER_FILE_BYTES) {
    throw new GeoTiffError(
      `This file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit for a raster ` +
        `import is ${MAX_RASTER_FILE_BYTES / 1024 / 1024} MB.`,
    );
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new GeoTiffError('This image could not be read.');
  }

  const scale = Math.min(1, RASTER_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  let base64: string;
  let mimeType: string;
  let width = bitmap.width;
  let height = bitmap.height;
  let downsampleNotice: string | undefined;

  if (scale < 1) {
    width = Math.max(1, Math.round(bitmap.width * scale));
    height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      throw new GeoTiffError('This browser could not render the image.');
    }
    context.drawImage(bitmap, 0, 0, width, height);
    const encoded = await canvasToCompressedImage(canvas);
    base64 = encoded.base64;
    mimeType = encoded.mimeType;
    downsampleNotice =
      `Downsampled from ${bitmap.width.toLocaleString()}×${bitmap.height.toLocaleString()} to ` +
      `${width.toLocaleString()}×${height.toLocaleString()} px, to keep the file light.`;
  } else {
    base64 = arrayBufferToBase64(await file.arrayBuffer());
    mimeType = file.type || (/\.png$/i.test(file.name) ? 'image/png' : 'image/jpeg');
  }
  bitmap.close();

  return { base64, mimeType, width, height, downsampleNotice };
}

/**
 * A plain PNG/JPG — no embedded georeferencing at all, unlike a GeoTIFF.
 * `center`/`zoom` are the map's current view — see `boundsForImageAtView`
 * for why they decide where this lands.
 */
export async function parsePlainImageFile(
  file: File,
  center: [number, number],
  zoom: number,
): Promise<ParsedPlainImage> {
  const decoded = await decodePlainImagePixels(file);
  const notices: string[] = [];
  if (decoded.downsampleNotice) notices.push(decoded.downsampleNotice);
  notices.push(
    'Not georeferenced — placed at the centre of your current map view, at roughly its own pixel ' +
      'size. Zoom and pan before adding it to land it closer to where it belongs.',
  );

  return {
    base64: decoded.base64,
    mimeType: decoded.mimeType,
    width: decoded.width,
    height: decoded.height,
    bandCount: decoded.mimeType === 'image/png' ? 4 : 3,
    bounds: boundsForImageAtView(decoded.width, decoded.height, center, zoom),
    notices,
  };
}

export interface DecodedRasterPixels {
  base64: string;
  mimeType: string;
  width: number;
  height: number;
}

/**
 * A raster's pixels with no placement opinion at all — not even
 * `parsePlainImageFile`'s own "centre of the current view" guess. For the
 * Georeference tool's own upload step: the whole point there is that the
 * user supplies the placement themselves, via control points, once this
 * returns — so unlike every other raster entry point in this app, a TIFF
 * does not need embedded georeferencing to be accepted here.
 */
export async function decodeRasterForGeoreferencing(file: File): Promise<DecodedRasterPixels> {
  const lower = file.name.toLowerCase();
  if (/\.(tif|tiff)$/i.test(lower)) {
    const decoded = await decodeGeoTiffPixels(file);
    return { base64: decoded.base64, mimeType: decoded.mimeType, width: decoded.width, height: decoded.height };
  }
  if (/\.(png|jpe?g)$/i.test(lower)) {
    const decoded = await decodePlainImagePixels(file);
    return { base64: decoded.base64, mimeType: decoded.mimeType, width: decoded.width, height: decoded.height };
  }
  throw new GeoTiffError('Use a PNG, JPG, or TIFF image.');
}

/** What `decodeGeoTiffPixels` needs again later for georeferencing — kept
 *  narrow rather than handing back the whole `georaster` object, which
 *  carries the full decoded pixel arrays a caller has no business touching
 *  twice. */
interface GeoTiffPixelMeta {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
  projection: number;
}

interface DecodedGeoTiffPixels {
  base64: string;
  mimeType: string;
  width: number;
  height: number;
  bandCount: number;
  notices: string[];
  meta: GeoTiffPixelMeta;
}

/**
 * The pixel-decode half of a GeoTIFF import — parse, resample, compress —
 * with no opinion on whether the file is usable for THIS caller's purpose.
 * `parseGeoTiffFile` (an import that needs the file's own embedded extent)
 * and `decodeRasterForGeoreferencing` (the Georeference tool's upload path,
 * which supplies its OWN extent via control points and never reads `meta`
 * at all) both call this rather than duplicating the `parseGeoraster` call.
 */
async function decodeGeoTiffPixels(
  file: File,
  options: { requireGeoreferencing?: boolean } = {},
): Promise<DecodedGeoTiffPixels> {
  if (file.size > MAX_RASTER_FILE_BYTES) {
    throw new GeoTiffError(
      `This file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit for a raster ` +
        `import is ${MAX_RASTER_FILE_BYTES / 1024 / 1024} MB.`,
    );
  }

  const buffer = await file.arrayBuffer();

  let meta;
  try {
    meta = await parseGeoraster(buffer);
  } catch {
    throw new GeoTiffError(
      'This file could not be read as a GeoTIFF. It may be a plain (non-georeferenced) ' +
        'TIFF — this import needs the geographic tags a GIS export includes.',
    );
  }

  // Checked BEFORE the (comparatively expensive) resample/encode below, so a
  // TIFF this caller cannot use fails fast rather than after doing all that
  // work. Skipped entirely for a caller that supplies its own placement.
  if (
    options.requireGeoreferencing &&
    (!Number.isFinite(meta.xmin) || !Number.isFinite(meta.ymin))
  ) {
    throw new GeoTiffError(
      'This TIFF has no embedded georeferencing, so there is nowhere to place it on the map.',
    );
  }
  if (!meta.values || meta.values.length === 0) {
    throw new GeoTiffError('This file has no pixel data to read.');
  }

  const notices: string[] = [];
  const { imageData, width, height } = buildImageData(meta);
  if (width < meta.width || height < meta.height) {
    notices.push(
      `Downsampled from ${meta.width.toLocaleString()}×${meta.height.toLocaleString()} to ` +
        `${width.toLocaleString()}×${height.toLocaleString()} px and compressed, to keep the file light.`,
    );
  }

  const { base64, mimeType } = await encodeCompressedImage(imageData);

  return {
    base64,
    mimeType,
    width,
    height,
    bandCount: meta.numberOfRasters,
    notices,
    meta: { xmin: meta.xmin, ymin: meta.ymin, xmax: meta.xmax, ymax: meta.ymax, projection: meta.projection },
  };
}

export async function parseGeoTiffFile(file: File): Promise<ParsedGeoTiff> {
  const decoded = await decodeGeoTiffPixels(file, { requireGeoreferencing: true });

  const notices = [...decoded.notices];
  const { bounds, notice } = boundsToWgs84(
    decoded.meta.xmin,
    decoded.meta.ymin,
    decoded.meta.xmax,
    decoded.meta.ymax,
    decoded.meta.projection,
  );
  if (notice) notices.push(notice);

  return {
    base64: decoded.base64,
    mimeType: decoded.mimeType,
    width: decoded.width,
    height: decoded.height,
    bandCount: decoded.bandCount,
    projectionEpsg: decoded.meta.projection,
    bounds,
    notices,
  };
}
